import React from "react";
import {
  HomeReel,
  ReelShareRecipient,
  socialFeedApi,
} from "../../../../services/socialFeedApi";
import { getAssetUrl } from "../utils";
import HomeModalShell from "./HomeModalShell";
import styles from "../Home.module.css";

interface ShareReelToCoachModalProps {
  token: string | null;
  reel: HomeReel | null;
  onClose: () => void;
}

const initials = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("") || "C";

const ShareReelToCoachModal: React.FC<ShareReelToCoachModalProps> = ({
  token,
  reel,
  onClose,
}) => {
  const [query, setQuery] = React.useState("");
  const [recipients, setRecipients] = React.useState<ReelShareRecipient[]>([]);
  const [selectedId, setSelectedId] = React.useState<string | null>(null);
  const [note, setNote] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [sending, setSending] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [success, setSuccess] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!reel || !token) return;
    const controller = new AbortController();
    const timeout = window.setTimeout(
      () => {
        setLoading(true);
        setError(null);
        socialFeedApi
          .getReelShareRecipients(
            token,
            reel.reelId,
            { query: query.trim(), limit: 20 },
            controller.signal,
          )
          .then((response) => setRecipients(response.recipients))
          .catch((loadError) => {
            if (!controller.signal.aborted)
              setError(
                loadError instanceof Error
                  ? loadError.message
                  : "Failed to load coaches.",
              );
          })
          .finally(() => {
            if (!controller.signal.aborted) setLoading(false);
          });
      },
      query ? 300 : 0,
    );
    return () => {
      window.clearTimeout(timeout);
      controller.abort();
    };
  }, [query, reel, token]);

  React.useEffect(() => {
    setQuery("");
    setRecipients([]);
    setSelectedId(null);
    setNote("");
    setError(null);
    setSuccess(null);
  }, [reel?.reelId]);

  if (!reel) return null;
  const selected =
    recipients.find((recipient) => recipient.id === selectedId) || null;
  const submit = async () => {
    if (!token || !selected || selected.alreadyShared || sending) return;
    setSending(true);
    setError(null);
    setSuccess(null);
    try {
      const delivery = await socialFeedApi.shareReelWithCoach(
        token,
        reel.reelId,
        {
          recipientId: selected.id,
          ...(note.trim() ? { note: note.trim() } : {}),
        },
      );
      setRecipients((items) =>
        items.map((item) =>
          item.id === selected.id ? { ...item, alreadyShared: true } : item,
        ),
      );
      setSuccess(
        delivery.alreadyShared
          ? `This reel was already sent to ${selected.name}.`
          : `Reel sent to ${selected.name}.`,
      );
    } catch (sendError) {
      setError(
        sendError instanceof Error
          ? sendError.message
          : "Failed to send this reel.",
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <HomeModalShell
      backdropClassName={styles.commentsBackdrop}
      panelClassName={`${styles.modalCard} ${styles.coachShareCard}`}
      ariaLabelledBy="coach-share-title"
      ariaDescribedBy="coach-share-description"
      onClose={onClose}
      closeDisabled={sending}
    >
      <div className={styles.modalHeader}>
        <div>
          <h2 id="coach-share-title" className={styles.modalTitle}>
            Send to Coach
          </h2>
          <p id="coach-share-description" className={styles.modalDescription}>
            Share your reel with a verified coach or recruiter.
          </p>
        </div>
        <button
          type="button"
          className={styles.modalClose}
          onClick={onClose}
          disabled={sending}
          aria-label="Close coach sharing"
        >
          ×
        </button>
      </div>

      <div className={styles.coachShareReelPreview}>
        {getAssetUrl(reel.thumbnailUrl) ? (
          <img src={getAssetUrl(reel.thumbnailUrl)} alt="" />
        ) : (
          <span aria-hidden="true">▶</span>
        )}
        <div>
          <strong>{reel.title}</strong>
          <small>By {reel.author.name}</small>
        </div>
      </div>

      <label className={`${styles.modalLabel} ${styles.coachShareSearch}`}>
        <span>Find a coach or organization</span>
        <input
          className={styles.modalInput}
          type="search"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Search coaches or colleges..."
          autoFocus
        />
      </label>

      <div
        className={styles.coachShareRecipients}
        role="radiogroup"
        aria-label="Coach recipients"
      >
        {loading ? (
          <p className={styles.coachShareState}>Loading coaches...</p>
        ) : null}
        {!loading && recipients.length === 0 ? (
          <p className={styles.coachShareState}>
            No available coaches match your search.
          </p>
        ) : null}
        {!loading &&
          recipients.map((recipient) => {
            const avatarUrl = getAssetUrl(recipient.profilePicture);
            return (
              <button
                key={recipient.id}
                type="button"
                role="radio"
                aria-checked={selectedId === recipient.id}
                disabled={recipient.alreadyShared || sending}
                className={`${styles.coachShareRecipient} ${selectedId === recipient.id ? styles.coachShareRecipientSelected : ""}`.trim()}
                onClick={() => {
                  setSelectedId(recipient.id);
                  setSuccess(null);
                }}
              >
                <span className={styles.coachShareAvatar}>
                  {avatarUrl ? (
                    <img src={avatarUrl} alt="" />
                  ) : (
                    initials(recipient.name)
                  )}
                </span>
                <span className={styles.coachShareRecipientCopy}>
                  <strong>{recipient.name}</strong>
                  <small>
                    {recipient.organization.name} ·{" "}
                    {recipient.role === "coach" ? "Coach" : "Recruiter"}
                  </small>
                </span>
                <span className={styles.coachShareRecipientStatus}>
                  {recipient.alreadyShared
                    ? "Already sent"
                    : selectedId === recipient.id
                      ? "Selected"
                      : ""}
                </span>
              </button>
            );
          })}
      </div>

      <label className={`${styles.modalLabel} ${styles.coachShareNote}`}>
        <span>
          Personal note <small>Optional</small>
        </span>
        <textarea
          className={styles.modalTextarea}
          value={note}
          maxLength={500}
          onChange={(event) => setNote(event.target.value)}
          placeholder="Introduce yourself and explain what you would like the coach to notice..."
        />
        <small>{note.length}/500</small>
      </label>

      {error ? (
        <p className={styles.coachShareError} role="alert">
          {error}
        </p>
      ) : null}
      {success ? (
        <p className={styles.coachShareSuccess} role="status">
          {success}
        </p>
      ) : null}
      <div className={styles.modalActions}>
        <button
          type="button"
          className={`${styles.modalButton} ${styles.modalButtonSecondary}`}
          onClick={onClose}
          disabled={sending}
        >
          Cancel
        </button>
        <button
          type="button"
          className={`${styles.modalButton} ${styles.modalButtonPrimary}`}
          onClick={() => void submit()}
          disabled={
            !selected || selected.alreadyShared || sending || Boolean(success)
          }
        >
          {sending ? "Sending..." : "Send Reel"}
        </button>
      </div>
    </HomeModalShell>
  );
};

export default React.memo(ShareReelToCoachModal);
